import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoadingSpinner from 'components/common/LoadingSpinner';
import useAccountRecovery from '../../../hooks/auth/useAccountRecovery';
import paths from '../../../routes/paths';

const VerificationCode = () => {
  const { verifyCode, isLoading, email } = useAccountRecovery();
  const location = useLocation();
  const [codes, setCodes] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  // Get email from location state or hook
  const userEmail = location.state?.email || email || 'your email';

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCodes = [...codes];
    newCodes[index] = value;
    setCodes(newCodes);
    setError('');

    // Auto-focus next input
    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        if (digits.length === 6) {
          const newCodes = [...codes];
          digits.forEach((digit, i) => {
            newCodes[i] = digit;
          });
          setCodes(newCodes);
          inputRefs.current[5]?.focus();
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6).split('');
    if (digits.length === 6) {
      const newCodes = [...codes];
      digits.forEach((digit, i) => {
        newCodes[i] = digit;
      });
      setCodes(newCodes);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = codes.join('');
    
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit verification code');
      return;
    }

    if (!/^\d{6}$/.test(code)) {
      setError('Verification code must be 6 digits');
      return;
    }

    setError('');
    await verifyCode(code);
  };

  const inputStyle = {
    width: '50px',
    height: '60px',
    fontSize: '1.5rem',
    textAlign: 'center',
    fontWeight: '600',
    border: '2px solid #b0b0b0',
    borderRadius: '8px',
    transition: 'border-color 0.2s ease'
  };

  return (
    <>
      <div className="text-center mb-4">
        <h5 className="mb-2">Verify your email</h5>
        <p className="text-muted mb-0 fs-10">
          We've sent a 6-digit verification code to <strong>{userEmail}</strong>
        </p>
        <p className="text-muted mb-0 fs-10 mt-2">
          Please enter the code below to verify your identity.
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        {/* Code Input */}
        <Form.Group className="mb-4">
          <div className="d-flex justify-content-center gap-2 mb-3">
            {codes.map((code, index) => (
              <Form.Control
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                value={code}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                className="text-center"
                style={inputStyle}
                disabled={isLoading}
                onFocus={(e) => {
                  e.target.style.borderColor = '#000';
                  e.target.select();
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#b0b0b0';
                }}
              />
            ))}
          </div>
          {error && (
            <Alert variant="danger" className="mb-0">
              <small>{error}</small>
            </Alert>
          )}
        </Form.Group>

        {/* Submit Button */}
        <Form.Group className="mb-3">
          <Button
            type="submit"
            variant="success"
            className="w-100 btn-lg fw-semibold d-flex align-items-center justify-content-center"
            style={{
              fontSize: '1.1rem',
              padding: '0.5rem 1.2rem',
              fontWeight: '600',
              textAlign: 'center'
            }}
            disabled={isLoading || codes.join('').length !== 6}
          >
            {isLoading ? (
              <LoadingSpinner size="sm" text="Verifying..." showText={false} />
            ) : (
              'Verify Code'
            )}
          </Button>
        </Form.Group>

        {/* Resend Code & Back Links */}
        <div className="text-center">
          <p className="fs-10 text-muted mb-2">
            Didn't receive the code?{' '}
            <Link 
              to={paths.simpleForgotPassword} 
              className="text-decoration-none"
              style={{ color: '#198754' }}
            >
              Resend code
            </Link>
          </p>
          <Link 
            to={paths.simpleLogin} 
            className="fs-10 text-decoration-none"
            style={{ color: '#6c757d' }}
          >
            <FontAwesomeIcon icon="chevron-left" className="me-1" />
            Back to login
          </Link>
        </div>
      </Form>
    </>
  );
};

export default VerificationCode;

